import {
  isBilingualMarketingPath,
  stripArabicPrefix,
} from '~/lib/marketing/routes';

import { defaultI18nNamespaces } from './i18n.settings';

/*
 * Which translation namespaces each area of the app preloads on the client.
 *
 * Every namespace used to be preloaded on every page: about 670 KB of JSON
 * (210 KB compressed) per visitor, and the app stayed suspended until all 24
 * files had downloaded. Each set below lists the namespaces that area's code
 * references (scanned from useTranslation / t('ns:key') / i18nKey usage), with
 * `common` first so it stays the default namespace. Anything else still loads
 * on demand, and I18nRouteNamespaces (root providers) loads an area's set when
 * a client-side navigation enters it.
 *
 * Unknown areas get every namespace, i.e. exactly the previous behaviour.
 */

/** Public marketing site (apex, non-tenant). */
export const marketingI18nNamespaces = [
  'common',
  'landing',
  'marketing',
  'auth',
  'account',
];

/** Tenant customer pages: landing, booking, packages, classes, profile, auth. */
export const tenantI18nNamespaces = [
  'common',
  'auth',
  'account',
  'marketing',
  'landing',
  'bookings',
  'packages',
  'court',
  'addons',
  'classes',
  'coaches',
  'members',
];

const ADMIN_PATH = /^\/(home|admin)(\/|$)/;
const AUTH_PATH = /^\/auth(\/|$)/;

/** Customer routes of the (tenant) route group, plus its landing page at '/'. */
const TENANT_CUSTOMER_PATH =
  /^\/($|(book|bookings|class-categories|coaches|contact|event|guests|members-only|membership|memberships|packages|pass|payment|profile|cookie-policy|privacy-policy|terms-of-service)(\/|$))/;

/**
 * Namespaces to preload for a pathname. `isTenant` is true when the request is
 * served for a tenant (subdomain, custom domain or a single-tenant deployment).
 */
export function i18nNamespacesFor(
  pathname: string,
  { isTenant }: { isTenant: boolean },
): string[] {
  if (ADMIN_PATH.test(pathname)) {
    return defaultI18nNamespaces;
  }

  if (AUTH_PATH.test(pathname)) {
    return tenantI18nNamespaces;
  }

  if (isTenant) {
    return TENANT_CUSTOMER_PATH.test(pathname)
      ? tenantI18nNamespaces
      : defaultI18nNamespaces;
  }

  if (isBilingualMarketingPath(stripArabicPrefix(pathname))) {
    return marketingI18nNamespaces;
  }

  return defaultI18nNamespaces;
}

/** True when this deployment renders a fixed tenant at the root (no subdomain). */
export const isSingleTenantDeployment = Boolean(
  process.env.NEXT_PUBLIC_DEFAULT_LANDING_PAGE,
);
